import { Field } from '@base-ui-components/react/field'
import { Input as BaseInput } from '@base-ui-components/react/input'
import { forwardRef, type HTMLAttributes, type InputHTMLAttributes } from 'react'
import styles from './text-field.module.css'

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, 'size'> {
  label?: string
  helperText?: string
  error?: boolean
  fullWidth?: boolean
  multiline?: boolean
  rows?: number
  variant?: 'outlined' | 'standard' | 'filled'
  size?: 'small' | 'medium'
  InputProps?: HTMLAttributes<HTMLDivElement> & {
    startAdornment?: React.ReactNode
    endAdornment?: React.ReactNode
  }
  inputProps?: InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>
  select?: boolean
}

const variantMap: Record<string, string | undefined> = {
  standard: styles.standard,
  filled: styles.filled,
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      helperText,
      error,
      fullWidth: _fullWidth,
      multiline,
      rows,
      variant = 'outlined',
      size = 'medium',
      InputProps,
      inputProps,
      className,
      id,
      ...rest
    },
    ref
  ) => {
    const wrapperClasses = [styles.textField, className]
      .filter(Boolean)
      .join(' ')

    const inputClasses = [
      styles.input,
      variantMap[variant],
      error ? styles.error : undefined,
      size === 'small' ? styles.sizeSmall : undefined,
      multiline ? styles.multiline : undefined,
    ]
      .filter(Boolean)
      .join(' ')

    const { startAdornment, endAdornment, ...inputPropsRest } = InputProps ?? {}

    const hasAdornment = Boolean(startAdornment || endAdornment)

    // When adornments are present, style goes to the wrapper div, not the inner input
    const { style: restStyle, ...restWithoutStyle } = rest as Record<string, unknown> & { style?: React.CSSProperties }

    const inputElement = multiline ? (
      <textarea
        ref={ref as React.Ref<HTMLTextAreaElement>}
        id={id}
        className={hasAdornment ? styles.adornmentInner : inputClasses}
        rows={rows}
        {...inputProps}
        {...(hasAdornment ? restWithoutStyle : (rest as Record<string, unknown>))}
        {...(!hasAdornment && restStyle ? { style: restStyle } : {})}
      />
    ) : (
      <BaseInput
        ref={ref as React.Ref<HTMLInputElement>}
        id={id}
        className={hasAdornment ? styles.adornmentInner : inputClasses}
        {...inputProps}
        {...(hasAdornment ? restWithoutStyle : rest)}
        {...(!hasAdornment && restStyle ? { style: restStyle } : {})}
      />
    )

    return (
      <Field.Root className={wrapperClasses} invalid={error}>
        {label ? <Field.Label className={styles.inputLabel}>{label}</Field.Label> : null}
        {hasAdornment ? (
          <div
            className={inputClasses}
            style={{ display: 'flex', alignItems: 'center', gap: 8, ...restStyle }}
            {...inputPropsRest}
          >
            {startAdornment}
            {inputElement}
            {endAdornment}
          </div>
        ) : (
          inputElement
        )}
        {helperText ? (
          error ? (
            <Field.Error className={[styles.helperText, styles.helperTextError].join(' ')} match>
              {helperText}
            </Field.Error>
          ) : (
            <Field.Description className={styles.helperText}>{helperText}</Field.Description>
          )
        ) : null}
      </Field.Root>
    )
  }
)

TextField.displayName = 'TextField'
